import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ogImageMeta } from "@/lib/og-images";

import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { RouteAnnouncer, SkipLink } from "../components/a11y";
import { PageTransition } from "../components/page-transition";
import { SiteNav } from "../components/site-nav";
import { WebMcpSiteTools } from "../components/webmcp-provider";



function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "author", content: "the Vibing Company®" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "the Vibing Company®" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@thevibingco" },
      { name: "twitter:creator", content: "@thevibingco" },
      { property: "og:locale", content: "en_US" },
      // Sitewide fallback share card; leaf routes override it with their own.
      ...ogImageMeta("home", "the Vibing Company® — postal-collage share card"),

      { name: "google-site-verification", content: "ZoLi4B0kSznWjICWl5D7Ll4sBh821nnOfNGzW1TbkiA" },
      // Page-specific title, description, og:*, and twitter:* tags live on leaf routes.
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      // Preconnect to the font CDN so the woff request fires while CSS parses.
      { rel: "preconnect", href: "https://fonts.cdnfonts.com", crossOrigin: "anonymous" },
      // Preload the nav logo (eager-rendered above the fold).
      { rel: "preload", as: "image", href: logo.url, fetchPriority: "high" },
      // AI-readable site index (llms.txt standard).
      { rel: "alternate", type: "text/markdown", href: "/llms.txt", title: "AI-readable site index" },
      { rel: "alternate", type: "text/markdown", href: "/llms-full.txt", title: "AI-readable full site content" },
    ],
    // Structured data lives on leaf routes (the homepage renders the full
    // Organization/WebSite/Service/WebAPI @graph); keeping it out of the root
    // avoids duplicate, incomplete identity blocks on every page.
    scripts: [],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Marketing surfaces share one persistent nav; app surfaces (admin, dashboard,
// auth, legal) render their own chrome.
const NAV_PREFIXES = [
  "/services",
  "/work",
  "/guides",
  "/contact",
  "/hiring",
  "/webmcp",
  "/easy-lover",
];

function GlobalNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const show =
    pathname === "/" || NAV_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  if (!show) return null;
  return <SiteNav />;
}

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Preserve auth callback hashes; they can contain one-time login tokens.
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Anchor links must land on their section, both
  // on first load (content mounts after hydration) and on in-page hash changes.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollToHash = (behavior: ScrollBehavior) => {
      const raw = window.location.hash.slice(1);
      if (!raw || raw.includes("access_token=")) return;
      let id: string;
      try {
        id = decodeURIComponent(raw);
      } catch {
        id = raw;
      }
      let tries = 0;
      const tick = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior, block: "start" });
          return;
        }
        if (tries++ < 180) requestAnimationFrame(tick);
      };
      tick();
    };

    scrollToHash("auto");
    const onHashChange = () => scrollToHash("smooth");
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Single global auth listener: re-run route gates on identity transitions
  // and clear protected query caches on sign-out. Ignores TOKEN_REFRESHED /
  // INITIAL_SESSION noise so we don't thrash on every tab focus.
  useEffect(() => {
    let mounted = true;
    import("@/integrations/supabase/client").then(({ supabase }) => {
      if (!mounted) return;
      const { data: sub } = supabase.auth.onAuthStateChange((event) => {
        if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
        router.invalidate();
        if (event === "SIGNED_OUT") {
          queryClient.clear();
        } else {
          queryClient.invalidateQueries();
        }
      });
      // Stash the unsubscribe on a closure-captured ref.
      cleanup = () => sub.subscription.unsubscribe();
    });
    let cleanup: (() => void) | null = null;
    return () => {
      mounted = false;
      cleanup?.();
    };
  }, [router, queryClient]);

  // Anticipatory route warming: once the page is idle after first paint, warm
  // the destinations in the global nav so the first click renders instantly.
  // Connection-aware — the queue no-ops on slow or data-saver connections.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      import("@/lib/prefetch-queue").then(({ enqueuePrefetch }) => {
        for (const to of ["/library", "/services", "/guides", "/contact"]) {
          enqueuePrefetch(`route:${to}`, () => router.preloadRoute({ to }), 0);
        }
      });
    }, 1200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [router]);




  return (
    <QueryClientProvider client={queryClient}>
      <SkipLink />
      <RouteAnnouncer />
      <WebMcpSiteTools />

      <GlobalNav />


      <div id="app-root">
        <PageTransition>
          <Outlet />
        </PageTransition>
      </div>
    </QueryClientProvider>
  );

}
